import React from 'react';
import { DashboardWidget } from './DashboardWidget';
import { TableWidget, TableRow } from './TableWidget';
import { InfoIcon } from './InfoIcon';
import { useUserPositions } from '../../hooks/useUserPositions';
import { useAuth } from '../../contexts/AuthContext';
import { fetchTokenPrice } from '../../api/userPositions';

interface PoolPositionsWidgetProps {
  className?: string;
}

export const PoolPositionsWidget: React.FC<PoolPositionsWidgetProps> = ({ className }) => {
  const { user } = useAuth();
  const { positionGroups, loading: hookLoading, error, refreshCurrentData } = useUserPositions(user?.walletAddress || null);
  const [rows, setRows] = React.useState<TableRow[]>([]);
  const [refreshing, setRefreshing] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);

  const loadData = async (bypassCache = false) => {
    try {
      setLoading(true);

      if (!positionGroups || positionGroups.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Check if we have actual positions (not just empty groups)
      const allPositions = positionGroups.flatMap(group => group.positions);
      if (allPositions.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Fetch AERO token price for claimable rewards calculation
      const aeroAddress = '0x940181a94a35a4569e4529a3cdfb74e38fd98631';
      let aeroPrice = 0;
      try {
        const aeroPriceData = await fetchTokenPrice(aeroAddress, { bypassCache });
        aeroPrice = aeroPriceData.priceUsd;
      } catch (err) {
        console.warn('Failed to fetch AERO price for claimable rewards:', err);
      }

      let totalDepositValue = 0;
      let totalClaimableValue = 0;
      const newRows: TableRow[] = allPositions.map(position => {
        const depositValue = position.positionValueUsd;
        const claimableValue = (position.claimableEmissions || 0) * aeroPrice;
        const totalValue = depositValue + claimableValue;

        totalDepositValue += depositValue;
        totalClaimableValue += claimableValue;

        return {
          token: position.poolName,
          amount: position.poolType === 'slipstream' ? 'Slipstream' : 'Basic',
          value: `$${totalValue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`,
          infoIcon: <InfoIcon
            tooltipData={{
              deposit: depositValue,
              claimable: claimableValue,
              total: totalValue
            }}
            onClick={() => {}}
          />
        };
      });

      const totalValue = totalDepositValue + totalClaimableValue;

      // Add total value row
      newRows.push({
        token: '',
        amount: '',
        value: `$${totalValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`,
        valueClass: 'total-row-highlight',
        infoIcon: <InfoIcon
          tooltipData={{
            deposit: totalDepositValue,
            claimable: totalClaimableValue,
            total: totalValue
          }}
          onClick={() => {}}
        />
      });

      setRows(newRows);
      setHasLoaded(true);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load pool positions:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setRefreshing(true);
    setHasLoaded(false);
    try {
      // refreshCurrentData already passes bypassCache=true, but we also need to refresh price
      await loadData(true); // bypassCache=true for manual refresh
      await refreshCurrentData();
    } catch (err) {
      console.error('Failed to refresh pool positions:', err);
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    if (!user?.walletAddress) return;
    // Wait for hook to completely finish loading AND have data
    if (!hookLoading && positionGroups && positionGroups.length > 0 && !hasLoaded) {
      loadData();
    }
  }, [user?.walletAddress, positionGroups, hookLoading, hasLoaded]);

  if (loading) {
    return (
      <div className={`dashboard-widget deposit-values ${className}`}>
        <div className="widget-header">
          <h3 className="widget-title">Deposit Values</h3>
        </div>
        <div className="widget-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your balances...</p>
          </div>
        </div>
        <div className="widget-refresh-bottom">
          <button
            className="refresh-button"
            disabled={true}
            style={{ pointerEvents: 'none' }}
          >
            Refresh
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated:<br/>{lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <DashboardWidget
      title="Deposit Values + Claimable Yield"
      loading={false}
      error={error || undefined}
      className={`deposit-values ${className}`}
      onRefresh={loading ? undefined : refreshData}
      refreshing={refreshing || loading}
      lastUpdated={lastUpdated ?? undefined}
      refreshButtonBottom={true}
    >
      <TableWidget
        rows={rows}
        showLabelColumn={false}
        columnHeaders={{
          token: 'Pool Name',
          amount: 'Type',
          value: 'Value'
        }}
      />
    </DashboardWidget>
  );
};
